import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    setColumns as setColumnsAction,
    setItems as setItemsAction,
    resetVirtualizedTable as resetVirtualizedTableAction,
    setSubmitHasFailed as setSubmitHasFailedAction
} from 'services/virtualizedTable/actions';
import DisableUserSelect from 'components/DisableUserSelect';
import { AppState } from 'services/types';
import ResetSelectedTableCellsOnSearchUpdate from 'components/ResetSelectedTableCellsOnSearchUpdate';
import { makeSelectVirtualizedTableSubmitHasFailed } from 'services/virtualizedTable/selectors';

import { VirtualizedTableProps } from './types';
import VirtualizedTableContext from './components/VirtualizedTableContext';
import VirtualizedTableBody from './components/VirtualizedTableBody';
import VirtualizedTableBulkEditModal from './components/VirtualizedTableBulkEditModal';
import VirtualizedTableCreateModal from './components/VirtualizedTableCreateModal';
import VirtualizedTableEditModal from './components/VirtualizedTableEditModal';
import VirtualizedTableDeleteModal from './components/VirtualizedTableDeleteModal';
import { MIN_WIDTH_FOR_INLINE_EDITING } from './constants';

type OwnProps = VirtualizedTableProps;

export interface DispatchProps {
    setColumns: typeof setColumnsAction;
    setItems: typeof setItemsAction;
    resetVirtualizedTable: typeof resetVirtualizedTableAction;
    setSubmitHasFailed: typeof setSubmitHasFailedAction;
}

export interface ConnectedProps {
    submitError: ReturnType<
        ReturnType<typeof makeSelectVirtualizedTableSubmitHasFailed>
    >;
}

export type Props = OwnProps & DispatchProps & ConnectedProps;

export const VirtualizedTableComponent = (
    props: Props
): React.ReactElement | null => {
    const {
        id,
        label,
        labelPlural,
        columns = [],
        selectable,
        editable,
        enableBulkEditing,
        enableCreating,
        enableItemEditing,
        enableDeleting,
        enableFooter,
        height,
        width,
        items,
        onExpand,
        onColumnResized,
        onItemValueChange,
        onItemsValueChange,
        onCreate,
        onItemUpdate,
        onDelete,
        setColumns,
        setItems,
        resetVirtualizedTable,
        modalSize,
        validateRow,
        hiddenFormFields,
        disabledFormFields,
        submitError,
        setSubmitHasFailed,
        loadNamespace,
        setItemFormat,
        itemValueFormat
    } = props;

    useEffect(() => {
        /**
         * Clean up old config to prevent it from displaying when navigating between pages with tables.
         */
        return (): void => {
            resetVirtualizedTable();
        };
    }, [resetVirtualizedTable, id]);

    useEffect(() => {
        /**
         * Store columns in redux to be used by child components.
         */
        setColumns(columns);
    }, [columns, setColumns]);

    useEffect(() => {
        /**
         * Store items in redux to be used by child components.
         */
        setItems(items);
    }, [items, setItems]);

    useEffect(() => {
        /**
         * Update items state when error occurs so that it does not display records that failed on submit.
         */
        if (submitError) {
            setItems(items);
            setSubmitHasFailed(false);
        }
    }, [setSubmitHasFailed, submitError, items, setItems]);

    const isMobile = useMemo((): boolean => {
        return width < MIN_WIDTH_FOR_INLINE_EDITING;
    }, [width]);

    return (
        <DisableUserSelect>
            {selectable && <ResetSelectedTableCellsOnSearchUpdate />}
            <VirtualizedTableContext.Provider
                value={{
                    label,
                    labelPlural,
                    selectable,
                    editable: editable && !isMobile,
                    enableItemEditing,
                    onExpand,
                    onColumnResized,
                    onItemValueChange,
                    onItemsValueChange,
                    onCreate,
                    onItemUpdate,
                    height,
                    enableFooter,
                    loadNamespace,
                    itemValueFormat
                }}
            >
                <VirtualizedTableBody height={height} width={width} />
            </VirtualizedTableContext.Provider>
            <VirtualizedTableContext.Provider
                value={{
                    label,
                    labelPlural,
                    onItemsValueChange,
                    onCreate,
                    onItemUpdate,
                    onDelete,
                    modalSize,
                    validateRow,
                    hiddenFormFields,
                    disabledFormFields,
                    setItemFormat
                }}
            >
                {enableCreating && <VirtualizedTableCreateModal />}
                {enableItemEditing && <VirtualizedTableEditModal />}
                {enableBulkEditing && <VirtualizedTableBulkEditModal />}
                {enableDeleting && <VirtualizedTableDeleteModal />}
            </VirtualizedTableContext.Provider>
        </DisableUserSelect>
    );
};

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    submitError: makeSelectVirtualizedTableSubmitHasFailed()
});

export default connect(mapStateToProps, {
    setColumns: setColumnsAction,
    setItems: setItemsAction,
    resetVirtualizedTable: resetVirtualizedTableAction,
    setSubmitHasFailed: setSubmitHasFailedAction
})(VirtualizedTableComponent);
